import { h } from '../dom';
import type { ScreenCtx } from './context';
import { screenHeader, type ScreenInstance } from './common';
import { CATEGORY_HINTS, CATEGORY_LABELS } from '../format';
import { CATEGORY_ORDER } from '../../meta/share';

const keys = (...k: string[]) => h('span', { class: 'sd-keys' }, k.map((x) => h('kbd', { text: x })));
const row = (k: HTMLElement, what: string, sub?: string) => h('div', { class: 'sd-howrow' }, [k, h('div', {}, [h('b', { text: what }), sub ? h('span', { text: sub }) : null])]);

export function howtoScreen(ctx: ScreenCtx): ScreenInstance {
  ctx.store.update((d) => { d.seenTutorial = true; });
  const punch = ctx.store.get().settings.punchMode;

  const flow = h('div', { class: 'sd-flow' }, [
    h('div', { class: 'sd-flow__wall' }, Array.from({ length: 9 }, (_, i) => h('span', { class: i === 2 ? 'is-pvw' : i === 0 ? 'is-pgm' : '', text: String(i + 1) }))),
    h('div', { class: 'sd-flow__arrow' }, [h('span', { text: '⇧ 3' }), h('i')]),
    h('div', { class: 'sd-flow__mon is-pvw' }, [h('span', { text: 'PVW' })]),
    h('div', { class: 'sd-flow__arrow' }, [h('span', { text: 'SPACE' }), h('i')]),
    h('div', { class: 'sd-flow__mon is-pgm' }, [h('span', { text: 'PGM' }), h('em', { text: 'ON AIR' })]),
  ]);

  const el = h('section', { class: 'sd-screen sd-page sd-howto' }, [
    screenHeader('HOW TO PLAY', { kicker: 'YOU ARE THE DIRECTOR', onBack: () => ctx.back() }),
    h('div', { class: 'sd-howto__body' }, [
      h('div', { class: 'sd-howto__intro' }, [
        h('p', { class: 'sd-howto__lede' }, ['The camera operators are AI. ', h('b', { text: 'You choose what goes to air.' }), ' Keep the action on PROGRAM, cut with rhythm, roll replays and dress the screen with graphics. Every decision is graded live.']),
        flow,
        h('button', { class: 'sd-btn sd-btn--primary sd-howto__train', attrs: { type: 'button', 'data-action': 'start-training' }, on: { click: () => ctx.startTraining() } },
          [h('span', { class: 'sd-golive__dot' }), ctx.store.get().training.done ? 'REPLAY THE FIRST BROADCAST' : 'LEARN BY DOING: THE FIRST BROADCAST']),
      ]),
      h('div', { class: 'sd-howto__grid' }, [
        h('div', { class: 'sd-howcard' }, [h('h3', { text: 'CUT' }),
          row(keys('1', '–', '9'), punch === 'hot' ? 'Punch camera to air' : 'Load camera to preview', punch === 'hot' ? 'Instant cut. Arcade style.' : 'Then TAKE it.'),
          row(keys('⇧', '1–9'), punch === 'hot' ? 'Load camera to preview' : 'Punch camera to air', 'Or click / shift-click a monitor.'),
          row(keys('SPACE'), 'TAKE', 'Cut preview to program.'),
          row(keys('M'), 'MIX', 'Half-second dissolve.'),
          row(keys('CLICK'), 'BENCH · CROWD', 'No hotkey: click their monitors.')]),
        h('div', { class: 'sd-howcard' }, [h('h3', { text: 'REPLAY' }),
          row(keys('R'), 'Roll the replay', 'Of the latest big moment.'),
          row(keys('1–9'), 'Change angle', 'While a replay is rolling.'),
          row(keys('R'), 'Back to live', 'Or ESC. Replays also end on their own.'),
          row(keys('ESC'), 'Pause', 'When no replay is rolling.')]),
        h('div', { class: 'sd-howcard' }, [h('h3', { text: 'GRAPHICS' }),
          row(keys('Q'), 'Lower third', 'Answers a booth player request first, then scorer / carded player / sub, else the ball carrier. At half-time: the HT score.'),
          row(keys('W'), 'Match stats', 'Press again to hide.'),
          row(keys('E'), 'Possession bar', 'Press again to hide.'),
          row(keys('S'), 'Scorebug on/off'),
          row(keys('A'), 'Clear graphics', 'Scorebug stays up.')]),
        h('div', { class: 'sd-howcard' }, [h('h3', { text: 'THE BOOTH' }),
          h('div', { class: 'sd-howquote', text: '“Can we get Okafor’s numbers up?”' }),
          row(keys('TAB'), 'Focus a request', 'Shift+Tab goes back.'),
          h('p', { class: 'sd-howcard__p', text: 'Commentators ask for shots, graphics and replays. Each request shows the key that answers it — do it before the bar runs out.' })]),
      ]),
      h('div', { class: 'sd-howto__graded' }, [
        h('div', { class: 'sd-section-head' }, [h('span', { text: "WHAT'S GRADED" })]),
        h('div', { class: 'sd-gradegrid' }, CATEGORY_ORDER.map((c) => h('div', { class: 'sd-gradetile' }, [h('b', { text: CATEGORY_LABELS[c] }), h('span', { text: CATEGORY_HINTS[c] })]))),
      ]),
      h('div', { class: 'sd-rules' }, [
        h('div', { class: 'sd-rule sd-rule--red' }, [h('span', { class: 'sd-rule__n', text: '1' }), h('b', { text: 'Never miss a goal.' }), h('span', { text: 'Key moments off PROGRAM = MISSED IT. Viewers leave.' })]),
        h('div', { class: 'sd-rule' }, [h('span', { class: 'sd-rule__n', text: '2' }), h('b', { text: 'Wide for play, tight for emotion.' }), h('span', { text: 'Hold MAIN WIDE in open play. Go tight for celebrations, cards and reactions.' })]),
        h('div', { class: 'sd-rule' }, [h('span', { class: 'sd-rule__n', text: '3' }), h('b', { text: 'Replay at the stoppage.' }), h('span', { text: 'Never over a live attack. Two angles earn a bonus. Goals must be replayed.' })]),
      ]),
    ]),
  ]);
  return { el };
}
